// Standard leave types this org grants. No dedicated "leave type" master
// exists yet, so this list is shared between the Leave Allocation admin UI
// (components/LeaveAllocationDialog.tsx) and the Request Leave form
// (components/RequestLeaveDialog.tsx) so both always offer the same options.
// Must match the labels used as keys in the backend's LEAVE_TYPE_OPTIONS map
// (clickup-integration/config/clickupIds.js) once that's filled in.
export const LEAVE_TYPES = ['Paid Leave', 'Unpaid Leave', 'Special Leave']

// Unpaid Leave is unlimited by design — no allocation record is required and
// there's no balance to check against (mirrors the skip in the backend's
// buildAndSubmitLeaveRequest, controller/leaveRequest.controller.js).
export const UNPAID_LEAVE_TYPE = 'Unpaid Leave'

export type HalfDayOption = 'full' | 'first' | 'second'

export const HALF_DAY_OPTIONS: { value: HalfDayOption; label: string }[] = [
  { value: 'full', label: 'Full Day' },
  { value: 'first', label: 'First Half' },
  { value: 'second', label: 'Second Half' },
]

// Mirrors utils/helpers.js#computeLeaveDays on the backend — used for the
// live "You are requesting N day(s)" preview in RequestLeaveDialog.tsx. The
// backend recomputes this itself as the authoritative value; this is purely
// for instant UI feedback before submit.
export function computeLeaveDaysPreview(
  fromDate: string,
  toDate: string,
  fromHalf: HalfDayOption,
  toHalf: HalfDayOption
): number | null {
  if (!fromDate || !toDate) return null
  const from = new Date(fromDate)
  const to = new Date(toDate)
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || to < from) {
    return null
  }

  const sameDay = from.toDateString() === to.toDateString()
  if (sameDay) {
    return fromHalf !== 'full' || toHalf !== 'full' ? 0.5 : 1
  }

  const diffDays =
    Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1
  let deduction = 0
  if (fromHalf !== 'full') deduction += 0.5
  if (toHalf !== 'full') deduction += 0.5
  return Math.max(0, diffDays - deduction)
}
